import { useEffect } from "react";

/**
 * Locks background page scrolling while `locked` is true.
 *
 * - Applies overflow hidden to both <html> and <body> for consistent behaviour
 *   across browsers (iOS Safari included).
 * - Compensates the removed scrollbar width with padding so the layout does not
 *   shift when the page scrollbar disappears.
 * - Restores the exact previous inline styles on cleanup, so stacking multiple
 *   scroll-locking overlays never breaks the page.
 */
export default function useScrollLock(locked: boolean): void {
  useEffect(() => {
    if (!locked) return;

    const html = document.documentElement;
    const { body } = document;

    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    const prevBodyPaddingRight = body.style.paddingRight;

    const scrollbarWidth = window.innerWidth - html.clientWidth;

    html.style.overflow = "hidden";
    body.style.overflow = "hidden";

    if (scrollbarWidth > 0) {
      const currentPadding =
        parseFloat(window.getComputedStyle(body).paddingRight) || 0;
      body.style.paddingRight = `${currentPadding + scrollbarWidth}px`;
    }

    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
      body.style.paddingRight = prevBodyPaddingRight;
    };
  }, [locked]);
}